// pages/SAPCustomizing.jsx
// SAP Customizing & Enhancement — daftar user exit / screen exit / BAdI yang
// terpasang di sistem SAP, hasil sync metadata dari MODACT/MODATTR/MODSAP/
// MODSAPT/TFDIR/D010INC/TADIR/TRDIR via RFC_READ_TABLE (backend: sapCustomizing.js).
//
// ⚠ BATAS YANG DISENGAJA — jangan dihapus dari UI:
// Halaman ini menampilkan METADATA saja: apa yang terpasang, di mana, aktif atau
// tidak, dan di include ABAP mana implementasinya. ISI KODE ABAP TIDAK tersedia
// (RFC_READ_REPORT di sistem ini dinonaktifkan/stub). Banner di bawah menyatakan
// batas itu secara eksplisit supaya pembaca tidak menyimpulkan lebih jauh dari
// yang datanya dukung — sama seperti paragraf "Batas informasi" pada dokumen RAG.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { useActiveDashboard } from '../context/ActiveDashboardContext';
// CronScheduler dipakai bersama dengan Monthly Report — jangan disalin ulang di
// sini. Ia sudah menangani validasi format cron, badge status, Run Now dengan
// konfirmasi, dan tampilan hasil terakhir.
import { CronScheduler } from './tmShared';
// Lapis Config / SPRO — berkas terpisah karena kolom, filter, dan aturan
// penyajian NULL-nya tidak berbagi apa pun dengan tabel enhancement.
import SAPConfigLayer from './SAPConfigLayer';

// ── useApi — pakai axios instance dari AuthContext (token via interceptor) ────
function useApi() {
  return useCallback(async (path, opts = {}) => {
    const method = (opts.method || 'GET').toLowerCase();
    const body   = opts.body ? JSON.parse(opts.body) : undefined;
    try {
      const res = (method === 'get' || method === 'delete')
        ? await axiosApi[method](path)
        : await axiosApi[method](path, body);
      return res.data;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message || 'API error');
    }
  }, []);
}

// Label tipe komponen — HARUS sinkron dengan COMP_TYPE_LABEL di backend
// (sapCustomizing.js & aiKbSap.js). Nilai yang benar-benar muncul di Q01: E/S/T.
const TYPE_LABEL = {
  E: 'Function exit',
  S: 'Screen exit',
  T: 'Table append',
  C: 'Menu exit',
  K: 'Keyword exit',
  P: 'BAdI',
};
const TYPE_COLOR = {
  E: 'rgb(var(--brand-600))', S: '#7c3aed', T: '#0d9488', C: '#ea580c', K: '#9333ea', P: '#dc2626',
};

// Tanggal SAP 'YYYYMMDD' → 'YYYY-MM-DD'. '00000000'/kosong → '—'.
function sapDate(s) {
  const v = String(s || '').trim();
  if (!/^\d{8}$/.test(v) || v === '00000000') return '—';
  return `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`;
}

// Status isi kode (src_state di sap_enhancement) — HARUS sinkron dengan
// statusTag() di aiKbSap.js dan readSource() di sapCustomizing.js.
// Pembedaan inilah inti halaman ini: "project AKTIF" TIDAK berarti ada kode yang
// berjalan. Di D01 terbukti 28 dari 64 include aktif justru nol baris kode.
const STATE_LABEL = {
  code:    'Berisi kode',
  empty:   'Include KOSONG',
  missing: 'Include tidak ada',
  denied:  'Tidak dapat dibaca',
  error:   'Gagal dibaca',
};
const STATE_COLOR = {
  code:    { c: '#15803d', bg: '#f0fdf4' },
  empty:   { c: '#b45309', bg: '#fffbeb' },
  missing: { c: '#b91c1c', bg: '#fef2f2' },
  denied:  { c: '#7c3aed', bg: '#f5f3ff' },
  error:   { c: '#b91c1c', bg: '#fef2f2' },
};

// Peringkat status untuk pengurutan kolom "Isi kode". Bukan alfabetis: urutan
// ini mengikuti seberapa penting bagi pembaca — yang berisi kode dulu, lalu yang
// aktif-tapi-kosong (temuan yang paling sering dicari), baru sisanya.
const STATE_RANK = { code: 1, empty: 2, missing: 3, denied: 4, error: 5 };

// Kategori BAdI. Ketiganya diturunkan dari sumber yang berbeda dan TIDAK boleh
// disamakan: hanya 'badi_classic' punya flag aktivasi sungguhan.
const BADI_KIND_LABEL = {
  badi_classic: 'BAdI klasik',
  badi_new:     'BAdI (spot)',
  source_enh:   'Source code',
};
const BADI_KIND_COLOR = {
  badi_classic: '#7c3aed',
  badi_new:     '#0891b2',
  source_enh:   '#c2410c',
};

// ── Modul SAP ────────────────────────────────────────────────────────────────
// app_component berisi application component PENUH dari DF14L.PS_POSID
// ('PM-WOC-MO'); kode modul adalah segmen PERTAMA. Diturunkan di sini, bukan disimpan di database, agar
// tidak ada dua sumber kebenaran. Kosong = enhancement tidak terpetakan ke
// application component SAP (biasanya berada di package pelanggan) — itu keadaan
// yang sah, bukan kegagalan sync, jadi ditampilkan sebagai '—' bukan error.
function moduleOf(r) {
  const c = String((r && r.app_component) || '').trim();
  return c ? c.split('-')[0].trim().toUpperCase() : '';
}

const MODULE_NAME = {
  MM: 'Materials Management', SD: 'Sales and Distribution',
  FI: 'Financial Accounting',  CO: 'Controlling',
  PP: 'Production Planning',   PM: 'Plant Maintenance',
  QM: 'Quality Management',    HR: 'Human Resources',
  PS: 'Project System',        WM: 'Warehouse Management',
  LE: 'Logistics Execution',   LO: 'Logistics General',
  BC: 'Basis Components',      CA: 'Cross-Application Components',
  EC: 'Enterprise Controlling', TR: 'Treasury', IM: 'Investment Management',
};

function parseArr(v) {
  if (Array.isArray(v)) return v;
  try { const a = JSON.parse(v); return Array.isArray(a) ? a : []; } catch (_) { return []; }
}

function Card({ children, style = {} }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: '1rem 1.25rem', ...style }}>
      {children}
    </div>
  );
}

// Kartu statistik sekaligus TOMBOL FILTER. Mengklik kartu yang sudah aktif akan
// MEMBATALKAN filternya — tanpa itu, user yang mengklik "Aktif tapi kosong" akan
// terjebak pada tampilan tersaring tanpa cara jelas untuk kembali.
function StatCard({ label, value, sub, color = 'rgb(var(--brand-600))', onClick, active = false }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      } : undefined}
      style={{
        background: active ? '#f8fafc' : '#fff',
        border: `1px solid ${active ? color : '#e2e8f0'}`,
        boxShadow: active ? `0 0 0 2px ${color}22` : '0 1px 3px rgba(0,0,0,0.04)',
        borderRadius: 10, padding: '0.45rem 0.75rem', minWidth: 110, flex: '0 1 auto',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'box-shadow .12s, border-color .12s',
      }}
    >
      <div style={{ fontSize: 17, fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginTop: 1 }}>{label}</div>
      {sub && <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function Badge({ children, color = '#64748b', bg = '#f1f5f9' }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 600, color, background: bg, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

// Penampil baris source ABAP. Diangkat menjadi komponen karena dipakai DUA kali
// dengan isi yang berbeda (include enhancement klasik dan class pool BAdI);
// menyalinnya akan membuat dua salinan aturan pewarnaan yang bisa berbeda diam-diam.
//
// ⚠ Yang WAJIB dijaga saat menyetel warna di sini adalah JARAK antara warna
// komentar dan warna kode. Pembedaan itu bukan hiasan: ia yang membuat "60 baris
// tanpa satu pun kode" (mis. ZXCOZU01) terlihat sekilas tanpa membaca satu baris
// pun. Membuat komentar terlalu pucat akan menghapus informasi itu.
//
// Skala TERANG, disamakan dengan blok kode markdown di components/AI/MarkdownRenderer.js
// (badan slate-50 #f8fafc, border slate-200 #e2e8f0, teks slate-800 #1e293b).
function SourceTable({ lines }) {
  if (!lines || !lines.length) return null;
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0',
      borderRadius: 10, overflow: 'hidden', maxHeight: 420, overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12 }}>
        <tbody>
          {lines.map(l => {
            // Komentar ABAP: '*' di kolom 1, atau '"' setelah spasi. Aturannya
            // sama dengan penghitung LINE_CODE di ABAP, supaya yang terlihat di
            // layar konsisten dengan angkanya.
            const txt = String(l.content ?? '');
            const trimmed = txt.replace(/^\s+/, '');
            const isComment = txt.startsWith('*') || trimmed.startsWith('"');
            const isBlank = !trimmed;
            return (
              <tr key={l.lineno}>
                <td style={{ padding: '1px 8px', textAlign: 'right', color: '#94a3b8',
                  userSelect: 'none', width: 44, verticalAlign: 'top',
                  borderRight: '1px solid #e2e8f0' }}>{l.lineno}</td>
                <td style={{ padding: '1px 10px', whiteSpace: 'pre',
                  color: isBlank ? '#cbd5e1' : (isComment ? '#64748b' : '#1e293b') }}>
                  {txt || ' '}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Salin source sebagai TEKS MURNI tanpa nomor baris: tujuan utamanya menempel
// kode ke SE38/SE80, dan nomor baris akan merusak setiap barisnya di sana.
function CopySourceButton({ lines, copied, setCopied, t, style = {} }) {
  if (!lines || !lines.length) return null;
  return (
    <button
      onClick={() => {
        const text = lines.map(l => String(l.content ?? '')).join('\n');
        // navigator.clipboard butuh konteks aman (HTTPS/localhost). Dashboard ini
        // dilayani lewat HTTPS, tapi kegagalan tetap ditangani agar tombolnya
        // tidak diam tanpa kabar.
        const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(text).then(done).catch(() => setCopied(false));
        } else {
          try {
            const ta = document.createElement('textarea');
            ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
            document.body.appendChild(ta); ta.select();
            document.execCommand('copy'); document.body.removeChild(ta); done();
          } catch (_) { setCopied(false); }
        }
      }}
      style={{ padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
        border: '1px solid #e2e8f0', background: '#fff',
        fontSize: 11, fontWeight: 600, color: copied ? '#15803d' : '#475569', ...style }}>
      {copied ? t('sapCust.srcCopied') : t('sapCust.srcCopy')}
    </button>
  );
}

// Warna status source — dipakai di tabel BAdI dan di drawer-nya.
const SRC_STATE_COLOR = {
  code: '#15803d', empty: '#b45309', missing: '#64748b',
  denied: '#b91c1c', error: '#b91c1c', auth: '#b91c1c',
};

// Satu kontrol sinkron: pilih lingkup + satu tombol, dengan progres langkah.
function SyncControl({ busy, step, onRun, srcOk, disabled }) {
  const [scope, setScope] = useState('meta');
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={scope} onChange={e => setScope(e.target.value)} disabled={busy}
        style={{ padding: '0.5rem 0.6rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff' }}>
        <option value="meta">Metadata (exits + BAdIs) — fast</option>
        <option value="tr">Transport history</option>
        <option value="src" disabled={!srcOk}>ABAP source (exits + BAdIs){srcOk ? '' : ' — not configured'}</option>
        <option value="all">Everything</option>
      </select>
      <button onClick={() => onRun(scope)} disabled={busy || disabled}
        style={{ padding: '0.5rem 1rem', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
          background: busy ? '#94a3b8' : '#059669', color: '#fff', cursor: (busy || disabled) ? 'not-allowed' : 'pointer' }}>
        {busy ? 'Syncing…' : 'Sync from SAP'}
      </button>
      {busy && step && <span style={{ fontSize: 12, color: '#64748b' }}>{step}</span>}
    </div>
  );
}

// ── Recently changed: include exit (TRDIR), proyek CMOD (MODATTR), BAdI ──────
const fmtD8 = d => (d && /^\d{8}$/.test(d) && d !== '00000000') ? `${d.slice(6, 8)}.${d.slice(4, 6)}.${d.slice(0, 4)}` : '';
const okD8 = d => !!d && /^\d{8}$/.test(d) && d !== '00000000';

function RecentCustChanges({ api, connId, rows, badi, onOpenEnh, onOpenBadi }) {
  const [dates, setDates] = useState(null);
  const [err, setErr] = useState('');
  const [days, setDays] = useState(90);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let alive = true;
    setDates(null); setErr('');
    api(`/api/sap/customizing/change-dates?connectionId=${connId}`)
      .then(d => alive && setDates(d)).catch(e => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api, connId]);

  const items = useMemo(() => {
    if (!dates) return [];
    const since = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10).replace(/-/g, '');
    const out = [];
    const incMap = new Map((dates.includes || []).map(i => [String(i.name || '').toUpperCase(), i]));
    // 1. Kode exit (include ZX*) — perubahan yang paling berarti.
    const seenInc = new Set();
    for (const r of rows || []) {
      for (const inc of parseArr(r.includes)) {
        const k = String(inc).toUpperCase();
        const d = incMap.get(k);
        if (!d || seenInc.has(k)) continue;
        const dt = okD8(d.udat) ? d.udat : d.cdat;
        if (!okD8(dt) || dt < since) continue;
        seenInc.add(k);
        out.push({ key: 'inc:' + k, kind: 'Exit code', name: k, sub: [r.project, r.component].filter(Boolean).join(' · '),
          user: d.unam || d.cnam, date: dt, open: () => onOpenEnh(r) });
      }
    }
    // 2. Proyek CMOD.
    for (const p of dates.projects || []) {
      const cand = [[p.udat, p.unam, 'Changed'], [p.adat, p.anam, 'Activated'], [p.cdat, p.cnam, 'Created']]
        .filter(x => okD8(x[0])).sort((a, b) => b[0].localeCompare(a[0]))[0];
      if (!cand || cand[0] < since) continue;
      const r = (rows || []).find(x => x.project === p.name);
      out.push({ key: 'prj:' + p.name, kind: 'CMOD project', name: p.name, sub: cand[2],
        user: cand[1], date: cand[0], open: r ? () => onOpenEnh(r) : null });
    }
    // 3. BAdI implementation.
    for (const b of badi || []) {
      const ch = okD8(b.changed_on) ? b.changed_on : '';
      const dt = ch || b.created_on;
      if (!okD8(dt) || dt < since) continue;
      out.push({ key: 'badi:' + b.kind + b.name, kind: 'BAdI', name: b.name, sub: b.definition || '',
        user: ch ? b.changed_by : b.created_by, date: dt, open: () => onOpenBadi(b) });
    }
    out.sort((a, b) => b.date.localeCompare(a.date) || a.name.localeCompare(b.name));
    return out;
  }, [dates, rows, badi, days, onOpenEnh, onOpenBadi]);

  const KIND_COLOR = { 'Exit code': '#0369a1', 'CMOD project': '#7c3aed', 'BAdI': '#b45309' };

  return (
    <Card style={{ marginBottom: 12, padding: '0.7rem 1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setOpen(o => !o)}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 700, fontSize: 13.5, color: '#0f172a', padding: 0 }}>
          {open ? '▾' : '▸'} Recently changed enhancements
        </button>
        <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
          exit code (TRDIR), CMOD projects (MODATTR), BAdIs · from last snapshot
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#64748b' }}>{dates ? `${items.length} item(s)` : ''}</span>
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          style={{ fontSize: 11.5, padding: '2px 4px', borderRadius: 6, border: '1px solid #cbd5e1', color: '#334155' }}>
          <option value={30}>30 days</option><option value={90}>90 days</option>
          <option value={180}>180 days</option><option value={365}>1 year</option><option value={1095}>3 years</option>
        </select>
      </div>
      {open && (
        <div style={{ marginTop: 8, maxHeight: 260, overflowY: 'auto' }}>
          {!dates && !err && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>Loading…</div>}
          {err && <div style={{ fontSize: 12.5, color: '#b91c1c' }}>{err}</div>}
          {dates && items.length === 0 && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>No changes in the last {days} days.</div>}
          {items.map(it => (
            <div key={it.key} onClick={it.open || undefined}
              style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '0.35rem 0.5rem', borderRadius: 6,
                cursor: it.open ? 'pointer' : 'default' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: KIND_COLOR[it.kind], minWidth: 88 }}>{it.kind}</span>
              <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12.5, fontWeight: 600, color: '#0f172a', minWidth: 150 }}>{it.name}</span>
              <span style={{ fontSize: 12, color: '#64748b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.sub}</span>
              <span style={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{it.user} · {fmtD8(it.date)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function SAPCustomizing({ embedded = false, connIdProp = '' } = {}) {
  const api = useApi();
  const { t } = useLang();

  const [conns, setConns]       = useState([]);
  const [connId, setConnId]     = useState('');
  const [rows, setRows]         = useState([]);
  const [status, setStatus]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [syncing, setSyncing]   = useState(false);
  // 2026-10-15: tarik data langsung dari SAP (sync/pull/cron/KB cleanup) khusus admin.
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem('auth_user') || '{}').role === 'admin'; } catch { return false; } })();
  const [err, setErr]           = useState('');
  const [msg, setMsg]           = useState('');

  // Filter
  const [q, setQ]               = useState('');
  const [fType, setFType]       = useState('');
  const [fProject, setFProject] = useState('');
  const [fModule, setFModule]   = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [detail, setDetail]     = useState(null);
  const [fState, setFState]     = useState('');
  // Default: kolom "Diubah oleh" secara MENURUN — perubahan terbaru di atas.
  // Itu pertanyaan yang paling sering muncul saat menelusuri masalah: apa yang
  // baru berubah di sistem ini?
  const [sortKey, setSortKey]   = useState('changed');
  const [sortDir, setSortDir]   = useState('desc');

  // Source ABAP dimuat MALAS (saat drawer dibuka), bukan bersama daftar:
  // payload-nya besar dan hanya dibutuhkan saat user benar-benar membukanya.
  const [srcLines, setSrcLines]   = useState([]);
  const [srcLoading, setSrcLoading] = useState(false);
  const [srcErr, setSrcErr]       = useState('');
  const [srcCopied, setSrcCopied] = useState(false);
  const [syncingSrc, setSyncingSrc] = useState(false);
  const [syncingTr, setSyncingTr]   = useState(false);
  const [syncingBadi, setSyncingBadi] = useState(false);
  const [badi, setBadi]             = useState([]);

  // Drawer source BAdI. State-nya DIPISAH dari srcLines/detail milik enhancement
  // klasik, tidak dipakai bersama: keduanya bisa terbuka dari tampilan yang
  // berbeda, dan berbagi satu state membuat drawer yang satu menampilkan sisa
  // kode drawer yang lain selama pemuatan.
  //
  // Satu BAdI menghasilkan BANYAK program (class pool + seluruh include
  // turunannya), jadi ada pilihan program aktif — berbeda dari enhancement yang
  // selalu satu include.
  const [badiDetail, setBadiDetail]       = useState(null);
  const [badiProg, setBadiProg]           = useState('');
  const [badiSrcLines, setBadiSrcLines]   = useState([]);
  const [badiSrcLoading, setBadiSrcLoading] = useState(false);
  const [badiSrcErr, setBadiSrcErr]       = useState('');
  const [badiSrcCopied, setBadiSrcCopied] = useState(false);
  const [syncingBadiSrc, setSyncingBadiSrc] = useState(false);
  // 'enh' = enhancement klasik CMOD/SMOD, 'badi' = BAdI & enhancement framework.
  // Dipisah menjadi dua TAMPILAN, bukan satu tabel gabungan: kolomnya berbeda
  // (project/include vs definisi/kelas) dan menggabungkannya membuat separuh sel
  // selalu kosong tanpa alasan yang terlihat dari layar.
  const [view, setView]             = useState('enh');
  const [sproFocus, setSproFocus]   = useState(0);

  // Ringkasan transport untuk SEMUA objek — satu panggilan, dipakai kolom tabel.
  // Rinciannya (daftar TR per objek) diambil terpisah saat drawer dibuka.
  const [trSummary, setTrSummary]   = useState({});
  const [trDetail, setTrDetail]     = useState(null);
  const [trLoading, setTrLoading]   = useState(false);

  // ── Pembersihan knowledge base ─────────────────────────────────────────────
  const [prunePlan, setPrunePlan] = useState(null);
  const [pruneBusy, setPruneBusy] = useState(false);

  const loadPrunePlan = useCallback(async () => {
    setPruneBusy(true); setErr(''); setMsg('');
    try {
      setPrunePlan(await api('/api/sap/customizing/kb-prune'));
    } catch (e) { setErr(e.message); }
    finally { setPruneBusy(false); }
  }, [api]);

  const runPrune = useCallback(async () => {
    // Konfirmasi memakai kalimat yang menyebut AKIBATNYA, bukan "Anda yakin?".
    // Prune menghapus dokumen di knowledge base dan tidak bisa dibatalkan;
    // pertanyaan yang tidak menyebut apa-apa akan selalu dijawab OK.
    if (!window.confirm(t('sapCust.kbPrune.confirm'))) return;
    setPruneBusy(true); setErr(''); setMsg('');
    try {
      const r = await api('/api/sap/customizing/kb-prune', { method: 'POST', body: '{}' });
      const n = (r && r.removed != null) ? r.removed : 0;
      setMsg(n ? `${t('sapCust.kbPrune.done')}: ${n}` : t('sapCust.kbPrune.clean'));
      setPrunePlan(null);
    } catch (e) { setErr(e.message); }
    finally { setPruneBusy(false); }
  }, [api, t]);

  // ── Deep link dari sumber AI ───────────────────────────────────────────────
  // Modal sumber KB menautkan ke sini agar verifikasi bisa diteruskan dari teks
  // yang dibaca model ke DATA aslinya.
  //
  // Dijalankan sebagai EFEK, bukan lazy initializer `useState(() => …)`, karena
  // DashboardContainer memakai lazy mount lalu `display:none`: halaman tidak
  // pernah di-unmount setelah pertama dibuka, sehingga initializer hanya jalan
  // pada kunjungan pertama seumur sesi. Alasan yang sama persis dengan
  // useTabDeepLink — lihat catatan panjang di hooks/useTabDeepLink.js.
  //
  // Penjagaan activePageKey juga wajib: tanpa itu, halaman ini yang masih hidup
  // di balik display:none akan ikut berganti filter setiap kali URL berubah.
  const [searchParams] = useSearchParams();
  const activePageKey  = useActiveDashboard();
  const navigate = useNavigate();
  // Mode tertanam (tab Enhancements di ABAP Workbench): sistem mengikuti pemilih induk.
  useEffect(() => { if (embedded && connIdProp) setConnId(String(connIdProp)); }, [embedded, connIdProp]);
  // Deep link versi tertanam: ?mode=enh&enhview=badi&q=&project=&module=
  useEffect(() => {
    if (!embedded || activePageKey !== 'sap_source' || searchParams.get('mode') !== 'enh') return;
    const v = searchParams.get('enhview');
    if (v === 'enh' || v === 'badi') setView(v);
    setQ(searchParams.get('q') || '');
    setFProject(searchParams.get('project') || '');
    setFModule(searchParams.get('module') || '');
  }, [embedded, activePageKey, searchParams]); // eslint-disable-line
  useEffect(() => {
    if (embedded || activePageKey !== 'sap_customizing') return;
    // 27-10-2026: enhancement pindah ke ABAP Workbench (tab Enhancements). User yang
    // punya sap_source dialihkan; yang hanya punya sap_customizing tetap di sini.
    {
      let u = {}; try { u = JSON.parse(localStorage.getItem('auth_user') || '{}'); } catch (_) { u = {}; }
      if (u.role === 'admin' || (u.permissions || []).includes('sap_source')) {
        const qp = new URLSearchParams(); qp.set('mode', 'enh');
        ['conn', 'view', 'q', 'project', 'module'].forEach(k => { const x = searchParams.get(k); if (x) qp.set(k === 'view' ? 'enhview' : k, x); });
        if (searchParams.get('view') === 'config') { navigate('/dashboard/sap-config' + (searchParams.get('conn') ? '?conn=' + searchParams.get('conn') : ''), { replace: true }); return; }
        navigate('/dashboard/sap-source?' + qp.toString(), { replace: true }); return;
      }
    }
    const conn = searchParams.get('conn');
    const v    = searchParams.get('view');
    const qs   = searchParams.get('q');
    const proj = searchParams.get('project');
    const mod  = searchParams.get('module');
    // Nilai asing divalidasi: `view` yang tidak dikenal membuat kedua panel
    // tidak dirender sama sekali — layar kosong tanpa pesan apa pun.
    if (conn && /^\d+$/.test(conn)) setConnId(String(conn));
    if (v === 'enh' || v === 'badi') setView(v);
    if (v === 'config') navigate('/dashboard/sap-config' + (conn ? '?conn=' + conn : ''));
    // Filter di-SET, termasuk ke string kosong bila param tidak ada. Kalau hanya
    // di-set saat ada isinya, filter dari kunjungan sebelumnya masih menempel
    // dan hasilnya tampak "objeknya tidak ada" padahal hanya tersaring.
    setQ(qs || '');
    setFProject(proj || '');
    setFModule(mod || '');
  }, [activePageKey, searchParams]); // eslint-disable-line

  // ── Muat daftar profil koneksi SAP ─────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    api('/api/sap/connections')
      .then(d => {
        if (!alive) return;
        const list = Array.isArray(d) ? d : (d.rows || d.connections || []);
        setConns(list);
        // Pilih profil pertama yang punya service path RFC_READ_TABLE — tanpa itu
        // sync pasti gagal. Perhatikan: /api/sap/connections mengembalikan
        // `pathReadtable` (camelCase), BUKAN nama kolom DB `path_readtable`.
        const first = list.find(c => c.pathReadtable) || list[0];
        // setConnId FUNGSIONAL, bukan nilai langsung: daftar koneksi datang dari
        // jaringan, jadi ia hampir selalu tiba SETELAH efek deep link berjalan.
        // Menimpanya dengan "profil pertama" akan membuang koneksi yang diminta
        // tautan — dan gejalanya halus: halaman terbuka dengan filter yang benar
        // tetapi pada SISTEM yang salah, sehingga objeknya tampak tidak ada.
        if (first) setConnId(prev => prev || String(first.id));
      })
      .catch(e => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [api]);

  const load = useCallback(async () => {
    if (!connId) return;
    setLoading(true); setErr('');
    try {
      const [d, s] = await Promise.all([
        api(`/api/sap/customizing/enhancements?connectionId=${connId}`),
        api(`/api/sap/customizing/status?connectionId=${connId}`),
      ]);
      setRows(d.rows || []);
      setStatus(s || null);
      // Ringkasan transport bersifat lintas sistem (bukan per connectionId), dan
      // boleh kosong bila sync transport belum pernah dijalankan — kegagalannya
      // TIDAK boleh menggagalkan pemuatan daftar enhancement.
      try {
        const tr = await api('/api/sap/customizing/transport-summary');
        setTrSummary((tr && tr.objects) || {});
      } catch (_) { setTrSummary({}); }
      // Sama halnya BAdI: tabelnya bisa belum pernah diisi pada instalasi lama,
      // dan itu TIDAK boleh menggagalkan pemuatan enhancement klasik.
      try {
        const b = await api(`/api/sap/customizing/badi?connectionId=${connId}`);
        setBadi((b && b.rows) || []);
      } catch (_) { setBadi([]); }
    } catch (e) { setErr(e.message); setRows([]); setStatus(null); }
    finally { setLoading(false); }
  }, [api, connId]);

  useEffect(() => { load(); }, [load]);

  // ── Satu tombol sinkron (27-10-2026) ──────────────────────────────────────
  // Menggantikan 5 tombol terpisah. Urutan tetap sama dengan tombol lama:
  // metadata exit → BAdI → riwayat transport → source exit → source BAdI.
  // Lingkup dipilih user; "Everything" menjalankan semuanya berurutan.
  const [syncAllBusy, setSyncAllBusy] = useState(false);
  const [syncStep, setSyncStep] = useState('');
  const doSyncAll = async (scope) => {
    if (!connId || syncAllBusy) return;
    const STEPS = {
      meta: [['Exit metadata', 'sync'], ['BAdI metadata', 'sync-badi']],
      tr:   [['Transport history', 'sync-transport']],
      src:  [['Exit source', 'sync-source'], ['BAdI source', 'sync-badi-source']],
    };
    let plan = scope === 'all' ? [...STEPS.meta, ...STEPS.tr, ...STEPS.src] : (STEPS[scope] || []);
    if (status && status.source_configured === false) plan = plan.filter(x => !/source/.test(x[1]));
    if (!plan.length) return;
    if (scope === 'all' && !window.confirm('Sync everything from SAP (exit & BAdI metadata, transport history, exit & BAdI source)? This can take several minutes.')) return;
    setSyncAllBusy(true); setErr(''); setMsg('');
    const done = [];
    try {
      for (let i = 0; i < plan.length; i++) {
        const [label, ep] = plan[i];
        setSyncStep(`${i + 1}/${plan.length} · ${label}…`);
        const r = await api(`/api/sap/customizing/${ep}?connectionId=${connId}`, { method: 'POST' });
        if (r && r.ok === false) throw new Error(`${label}: ${r.aborted || t('sapCust.srcAborted')}`);
        const n = ep === 'sync' ? `${r.rows_enh ?? 0} components`
          : ep === 'sync-badi' ? `${r.total ?? 0} BAdIs`
          : ep === 'sync-transport' ? `${r.trkorrs ?? 0} TRs`
          : `${r.code ?? 0} with code / ${r.read ?? 0} read`;
        done.push(`${label}: ${n}`);
      }
      setMsg('Sync finished — ' + done.join(' · '));
    } catch (e) { setErr((done.length ? 'Done so far: ' + done.join(' · ') + ' — ' : '') + e.message); }
    finally { setSyncAllBusy(false); setSyncStep(''); await load(); }
  };

  const doSync = async () => {
    if (!connId || syncing) return;
    setSyncing(true); setErr(''); setMsg('');
    try {
      const r = await api(`/api/sap/customizing/sync?connectionId=${connId}`, { method: 'POST' });
      setMsg(t('sapCust.syncDone')
        .replace('{enh}', r.rows_enh ?? 0)
        .replace('{raw}', r.rows_raw ?? 0)
        .replace('{kb}', r.kb_indexed ?? 0));
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSyncing(false); }
  };

  const doSyncSource = async () => {
    if (!connId || syncingSrc) return;
    setSyncingSrc(true); setErr(''); setMsg('');
    try {
      const r = await api(`/api/sap/customizing/sync-source?connectionId=${connId}`, { method: 'POST' });
      // ok:false berarti pembacaan DIHENTIKAN (mis. autentikasi ditolak) dan
      // hasilnya TIDAK lengkap — jangan tampilkan sebagai keberhasilan.
      if (r.ok === false) setErr(r.aborted || t('sapCust.srcAborted'));
      else setMsg(t('sapCust.srcDone')
        .replace('{code}', r.code ?? 0)
        .replace('{empty}', r.empty ?? 0)
        .replace('{read}', r.read ?? 0));
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSyncingSrc(false); }
  };

  const doSyncTransport = async () => {
    if (!connId || syncingTr) return;
    setSyncingTr(true); setErr(''); setMsg('');
    try {
      const r = await api(`/api/sap/customizing/sync-transport?connectionId=${connId}`, { method: 'POST' });
      setMsg(t('sapCust.trDone')
        .replace('{ver}', r.versions ?? 0)
        .replace('{tr}', r.trkorrs ?? 0)
        .replace('{imp}', r.imports ?? 0));
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSyncingTr(false); }
  };

  const doSyncBadi = async () => {
    if (!connId || syncingBadi) return;
    setSyncingBadi(true); setErr(''); setMsg('');
    try {
      const r = await api(`/api/sap/customizing/sync-badi?connectionId=${connId}`, { method: 'POST' });
      setMsg(t('sapCust.badiDone')
        .replace('{total}', r.total ?? 0)
        .replace('{classic}', r.classic ?? 0)
        .replace('{new}', r.badi_new ?? 0)
        .replace('{src}', r.source_enh ?? 0));
      setView('badi');   // hasilnya langsung terlihat, tidak perlu dicari sendiri
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSyncingBadi(false); }
  };

  const doSyncBadiSource = async () => {
    if (!connId || syncingBadiSrc) return;
    setSyncingBadiSrc(true); setErr(''); setMsg('');
    try {
      const r = await api(`/api/sap/customizing/sync-badi-source?connectionId=${connId}`, { method: 'POST' });
      // ok:false berarti pembacaan DIHENTIKAN (mis. autentikasi ditolak) dan
      // hasilnya TIDAK lengkap — jangan tampilkan sebagai keberhasilan.
      if (r.ok === false) setErr(r.aborted || t('sapCust.srcAborted'));
      else setMsg(t('sapCust.badiSrcDone')
        .replace('{read}', r.read ?? 0)
        .replace('{code}', r.code ?? 0)
        .replace('{prog}', r.programs ?? 0)
        .replace('{lines}', r.lines ?? 0));
      setView('badi');
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSyncingBadiSrc(false); }
  };

  // ── Muat riwayat transport saat drawer dibuka ──────────────────────────────
  useEffect(() => {
    setTrDetail(null);
    if (!detail) return;
    const inc = parseArr(detail.includes)[0];
    if (!inc) return;
    let alive = true;
    setTrLoading(true);
    api(`/api/sap/customizing/transport?program=${encodeURIComponent(inc)}`)
      .then(d => { if (alive) setTrDetail(d); })
      .catch(() => { if (alive) setTrDetail(null); })
      .finally(() => { if (alive) setTrLoading(false); });
    return () => { alive = false; };
  }, [detail, api]);

  // ── Muat source ABAP saat drawer dibuka ────────────────────────────────────
  // Dimuat untuk src_lines > 0 apa pun statusnya — termasuk 'empty'. Justru
  // include KOSONG yang isinya menarik: 60 baris komentar pada ZXCOZU01 adalah
  // logika yang pernah berjalan lalu dimatikan, dan itu tidak terlihat kalau
  // hanya status yang ditampilkan.
  useEffect(() => {
    // srcCopied ikut di-reset: tanpa ini tombol masih berbunyi "Tersalin" saat
    // drawer sudah berpindah ke include LAIN, sehingga user percaya sudah
    // menyalin kode yang sebenarnya belum pernah ia salin.
    setSrcLines([]); setSrcErr(''); setSrcCopied(false);
    if (!detail || !connId) return;
    const inc = parseArr(detail.includes)[0];
    if (!inc || !detail.src_lines) return;
    let alive = true;
    setSrcLoading(true);
    api(`/api/sap/customizing/source?connectionId=${connId}&program=${encodeURIComponent(inc)}`)
      .then(d => { if (alive) setSrcLines(d.lines || []); })
      .catch(e => { if (alive) setSrcErr(e.message); })
      .finally(() => { if (alive) setSrcLoading(false); });
    return () => { alive = false; };
  }, [detail, connId, api]);

  // ── Pilih program awal saat drawer BAdI dibuka ─────────────────────────────
  // Yang dipilih adalah program PERTAMA yang bukan class pool: class pool sendiri
  // hanya ~20 baris kerangka hasil generate SAP (deklarasi include), sementara
  // yang dicari orang saat membuka BAdI adalah implementasi method-nya.
  useEffect(() => {
    setBadiSrcLines([]); setBadiSrcErr(''); setBadiSrcCopied(false);
    if (!badiDetail) { setBadiProg(''); return; }
    const progs = parseArr(badiDetail.src_progs);
    const pool = String(badiDetail.src_pool || '').toUpperCase();
    setBadiProg(progs.find(p => String(p).toUpperCase() !== pool) || progs[0] || '');
  }, [badiDetail]);

  // ── Muat source BAdI saat program dipilih ──────────────────────────────────
  // Dimuat MALAS dan per program: satu kelas bisa menghasilkan 24 program / 672
  // baris, dan menariknya sekaligus akan membuat drawer berat tanpa alasan.
  useEffect(() => {
    setBadiSrcLines([]); setBadiSrcErr(''); setBadiSrcCopied(false);
    if (!badiDetail || !connId || !badiProg) return;
    let alive = true;
    setBadiSrcLoading(true);
    api(`/api/sap/customizing/source?connectionId=${connId}&program=${encodeURIComponent(badiProg)}`)
      .then(d => { if (alive) setBadiSrcLines(d.lines || []); })
      .catch(e => { if (alive) setBadiSrcErr(e.message); })
      .finally(() => { if (alive) setBadiSrcLoading(false); });
    return () => { alive = false; };
  }, [badiDetail, badiProg, connId, api]);

  // ── Turunan ────────────────────────────────────────────────────────────────
  const projects = useMemo(
    () => [...new Set(rows.map(r => r.project).filter(Boolean))].sort(),
    [rows]);

  const badiStats = useMemo(() => {
    const byKind = {};
    badi.forEach(b => { byKind[b.kind] = (byKind[b.kind] || 0) + 1; });
    // Hanya BAdI KLASIK punya flag aktivasi yang sungguhan (SXC_ATTR.ACTIVE).
    // Untuk kategori ENH*, ENHOBJ tidak menyimpan status aktif sama sekali — jadi
    // "tidak aktif" HANYA dihitung dari yang klasik, bukan dari seluruh baris.
    const classic = badi.filter(b => b.kind === 'badi_classic');
    return {
      total: badi.length, byKind,
      classicOff: classic.filter(b => !b.is_active).length,
      localTmp: badi.filter(b => String(b.package || '').toUpperCase() === '$TMP').length,
    };
  }, [badi]);

  // Modul yang benar-benar muncul di data, terurut. Baris tanpa modul TIDAK
  // menghasilkan entri kosong di dropdown — untuk itu ada opsi khusus '(none)'.
  const modules = useMemo(
    () => [...new Set(rows.map(moduleOf).filter(Boolean))].sort(),
    [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (activeOnly && !r.is_active) return false;
      if (fType && r.comp_type !== fType) return false;
      if (fState && String(r.src_state || '') !== fState) return false;
      if (fProject && r.project !== fProject) return false;
      // '(none)' adalah nilai sentinel untuk baris tanpa modul; '' berarti tanpa filter.
      if (fModule && (fModule === '(none)' ? moduleOf(r) !== '' : moduleOf(r) !== fModule)) return false;
      if (!needle) return true;
      // Cari lintas kolom yang paling sering jadi titik awal penelusuran.
      const hay = [r.component, r.enhancement, r.project, r.enh_text,
        r.func_group, parseArr(r.includes).join(' '), r.badi_imp,
        r.app_component, r.app_comp_text, MODULE_NAME[moduleOf(r)] || ''].join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q, fType, fProject, fModule, activeOnly, fState]);

  // ── Pengurutan ─────────────────────────────────────────────────────────────
  // Nilai kosong SELALU ditaruh di bawah, tidak tergantung arah. Kalau ikut arah,
  // menaik akan menampilkan seluruh baris tanpa tanggal lebih dulu — dan itu
  // menyembunyikan justru data yang ingin dilihat.
  const sortVal = useCallback((r) => {
    switch (sortKey) {
      case 'component':   return r.component || '';
      case 'type':        return r.comp_type || '';
      case 'enhancement': return r.enhancement || '';
      case 'project':     return r.project || '';
      case 'module':      return moduleOf(r);
      case 'include':     return parseArr(r.includes)[0] || '';
      // Peringkat status dulu, lalu jumlah baris kode menurun di dalam peringkat
      // yang sama — agar exit dengan kode terbanyak muncul di atas.
      case 'srcState':    return r.src_state ? (STATE_RANK[r.src_state] || 9) * 100000 + (99999 - (r.src_code || 0)) : '';
      // changed_on berformat YYYYMMDD, jadi urut leksikografis = urut kronologis.
      // '00000000' dan kosong dianggap tidak ada tanggal.
      case 'changed':
      default: {
        const d = String(r.changed_on || '').trim();
        return (/^\d{8}$/.test(d) && d !== '00000000') ? d : '';
      }
    }
  }, [sortKey]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = sortVal(a), vb = sortVal(b);
      const ea = va === '' || va == null, eb = vb === '' || vb == null;
      if (ea && eb) return 0;
      if (ea) return 1;
      if (eb) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [filtered, sortDir, sortVal]);

  const toggleSort = (key) => {
    if (key === sortKey) { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); return; }
    setSortKey(key);
    // Tanggal & status lebih berguna dibaca menurun lebih dulu; teks menaik.
    setSortDir(key === 'changed' || key === 'srcState' ? 'desc' : 'asc');
  };

  const stats = useMemo(() => {
    const active = rows.filter(r => r.is_active).length;
    const byType = {}, byState = {}, byModule = {};
    rows.forEach(r => {
      byType[r.comp_type] = (byType[r.comp_type] || 0) + 1;
      const m = moduleOf(r);
      if (m) byModule[m] = (byModule[m] || 0) + 1;
      const st = String(r.src_state || '');
      if (st) byState[st] = (byState[st] || 0) + 1;
    });
    // Yang paling perlu disorot: komponen AKTIF yang include-nya KOSONG.
    // Inilah yang dulu tampak seperti "ada logika kustom" padahal tidak ada.
    const activeEmpty = rows.filter(r => r.is_active && r.src_state === 'empty').length;
    // noModule dihitung eksplisit supaya cakupan pemetaan modul terlihat, bukan
    // tersembunyi sebagai selisih yang harus dihitung sendiri oleh pembaca.
    const noModule = rows.filter(r => !moduleOf(r)).length;
    return { total: rows.length, active, projects: projects.length, byType, byState, activeEmpty,
             byModule, modules: Object.keys(byModule).length, noModule };
  }, [rows, projects]);

  const conn = conns.find(c => String(c.id) === String(connId));

  // ── Filter dari kartu statistik ────────────────────────────────────────────
  // Semua filter (termasuk pencarian teks) dibersihkan lebih dulu, lalu hanya
  // yang diminta dipasang. Menumpuk filter dari beberapa kartu mudah menghasilkan
  // tabel kosong tanpa sebab yang jelas — dan user tidak punya cara menebak
  // kombinasi mana yang sedang aktif.
  const clearFilters = () => { setQ(''); setFType(''); setFProject(''); setFModule(''); setFState(''); setActiveOnly(false); };
  const noFilter = !q && !fType && !fProject && !fModule && !fState && !activeOnly;

  const pickType  = (ty) => { const on = fType === ty && !activeOnly && !fState; clearFilters(); if (!on) setFType(ty); };
  const pickModule = (m) => { const on = fModule === m && !activeOnly && !fState && !fType; clearFilters(); if (!on) setFModule(m); };
  const pickState = (st) => { const on = fState === st && !activeOnly && !fType; clearFilters(); if (!on) setFState(st); };
  const pickActive = () => { const on = activeOnly && !fType && !fState; clearFilters(); if (!on) setActiveOnly(true); };
  const pickActiveEmpty = () => {
    const on = activeOnly && fState === 'empty';
    clearFilters();
    if (!on) { setActiveOnly(true); setFState('empty'); }
  };

  return (
    <div style={embedded ? {} : { padding: '1.25rem', maxWidth: 1500, margin: '0 auto' }}>

      {/* ── Header ── */}
      <div style={embedded
        ? { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14 }
        : { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 14, background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        {embedded && (
          <div style={{ flex: '1 1 100%', fontSize: 13, color: '#475569' }}>
            Inventory of user exits (CMOD/SMOD), screen exits, table appends and BAdI implementations — metadata snapshot plus exit source.
          </div>
        )}
        {!embedded && (<>
        <div style={{ flex: '1 1 260px' }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>
            {t('page.sapCustomizing.title')}
          </h1>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>
            {t('page.sapCustomizing.subtitle')}
          </div>
        </div>

        <select
          value={connId}
          onChange={e => setConnId(e.target.value)}
          style={{ padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1',
            fontSize: 13, background: '#fff', minWidth: 200 }}
        >
          {conns.map(c => (
            <option key={c.id} value={c.id}>
              {[c.landscape, c.environment, c.sid].filter(Boolean).join(' / ')} — {c.host} ({c.client})
            </option>
          ))}
        </select>

        <button
          onClick={() => navigate('/dashboard/sap-config?view=recent' + (connId ? '&conn=' + connId : ''))}
          title="Open SPRO navigator → Recently changed (table change log)"
          style={{ padding: '0.55rem 1rem', borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: '1px solid #fde68a', background: '#fffbeb', color: '#92400e', cursor: 'pointer' }}
        >
          Recent SPRO changes
        </button>
        </>)}

        {isAdmin && (
          <SyncControl busy={syncAllBusy} step={syncStep} onRun={doSyncAll}
            srcOk={!(status && status.source_configured === false)} disabled={!connId} />
        )}
      </div>

      {/* ── Pemisah tampilan ──
          Tab bar SELALU tampil. Sebelumnya seluruh bar bersyarat `badi.length`,
          dan itu sudah tidak boleh: lapis config tidak ada hubungannya dengan
          BAdI, sehingga di sistem yang belum pernah sync BAdI tab Config akan
          ikut hilang tanpa satu pun petunjuk di layar tentang sebabnya.

          Yang bersyarat sekarang hanya ENTRI BAdI-nya, dengan alasan asli yang
          tetap berlaku: menampilkan tab BAdI kosong membuat user menebak apakah
          artinya "tidak ada BAdI" atau "belum ditarik" — dua hal yang sangat
          berbeda. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[['enh', t('sapCust.viewEnh'), rows.length],
          ...(badi.length ? [['badi', t('sapCust.viewBadi'), badi.length]] : []),
          ...(embedded ? [] : [['config', 'SAP Configuration (SPRO) ↗', null]])]
          .map(([k, label, n]) => (
              <button key={k} onClick={() => k === 'config' ? navigate('/dashboard/sap-config' + (connId ? '?conn=' + connId : '')) : setView(k)}
                style={{ padding: '0.45rem 0.9rem', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer',
                  border: view === k ? '1px solid rgb(var(--brand-600))' : '1px solid #cbd5e1',
                  background: view === k ? 'rgb(var(--brand-50))' : '#fff',
                  color: view === k ? 'rgb(var(--brand-700))' : '#475569' }}>
                {/* Cacah OPSIONAL: tab config tidak punya angka yang bisa
                    dipercaya sebelum summary-nya dimuat, dan "(0)" akan terbaca
                    sebagai "tidak ada data" padahal artinya "belum diambil". */}
                {label}{n == null ? '' : ` (${n})`}
              </button>
            ))}
      </div>

      {err && (
        <Card style={{ marginBottom: 12, background: '#fef2f2', borderColor: '#fecaca' }}>
          <div style={{ fontSize: 13, color: '#b91c1c' }}>{err}</div>
        </Card>
      )}
      {msg && (
        <Card style={{ marginBottom: 12, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
          <div style={{ fontSize: 13, color: '#15803d' }}>{msg}</div>
        </Card>
      )}

      {view !== 'config' && (<>
      {/* ── Batas informasi — pagar yang sama dengan dokumen RAG ──
          Teksnya ADAPTIF: selama belum ada source yang dibaca, halaman ini memang
          metadata saja. Begitu ada, klaim "kode tidak tersedia" jadi salah — dan
          teks yang salah di layar lebih berbahaya daripada tidak ada teks. */}
      {!embedded && <Card style={{ marginBottom: 14, background: '#fffbeb', borderColor: '#fde68a', padding: '0.7rem 1rem' }}>
        <div style={{ fontSize: 12.5, color: '#92400e', lineHeight: 1.5 }}>
          <strong>{t('sapCust.limitTitle')}</strong>{' '}
          {Object.keys((status && status.src) || {}).length
            ? t('sapCust.limitBodySrc')
            : t('sapCust.limitBody')}
        </div>
      </Card>}

      {/* ── Statistik ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <StatCard label={t('sapCust.stat.total')} value={stats.total}
          onClick={clearFilters} active={noFilter} />
        <StatCard label={t('sapCust.stat.active')} value={stats.active} color="#16a34a"
          onClick={pickActive} active={activeOnly && !fType && !fState} />
        {/* Jumlah project TIDAK bisa dijadikan satu nilai filter, jadi kartunya
            sengaja tidak dapat diklik — daripada tombol yang tak melakukan apa pun. */}
        <StatCard label={t('sapCust.stat.project')} value={stats.projects} color="#7c3aed" />
        <StatCard label={t('sapCust.stat.module')} value={stats.modules} color="#0891b2"
          sub={stats.noModule ? t('sapCust.stat.moduleSub').replace('{n}', stats.noModule) : ''} />
        {Object.entries(stats.byType).sort((a, b) => b[1] - a[1]).map(([ty, n]) => (
          <StatCard key={ty} label={TYPE_LABEL[ty] || `Tipe ${ty || '?'}`} value={n}
            color={TYPE_COLOR[ty] || '#64748b'}
            onClick={() => pickType(ty)} active={fType === ty && !activeOnly && !fState} />
        ))}
      </div>

      {/* Statistik isi kode — hanya tampil setelah source pernah dibaca. */}
      {!!Object.keys(stats.byState).length && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {['code', 'empty', 'missing', 'denied', 'error'].filter(k => stats.byState[k]).map(k => (
            <StatCard key={k} label={STATE_LABEL[k]} value={stats.byState[k]}
              color={(STATE_COLOR[k] || {}).c || '#64748b'}
              onClick={() => pickState(k)} active={fState === k && !activeOnly && !fType} />
          ))}
          {stats.activeEmpty > 0 && (
            <StatCard label={t('sapCust.stat.activeEmpty')} value={stats.activeEmpty}
              sub={t('sapCust.stat.activeEmptySub')} color="#b45309"
              onClick={pickActiveEmpty} active={activeOnly && fState === 'empty'} />
          )}
        </div>
      )}

      {/* ── Perubahan terbaru (dari snapshot) ── */}
      {connId && (
        <RecentCustChanges api={api} connId={connId} rows={rows} badi={badi}
          onOpenEnh={r => setDetail(r)} onOpenBadi={b => { setView('badi'); if (b.impl_class) setBadiDetail(b); }} />
      )}

      {/* ── Info snapshot ── */}
      {status && status.last_sync && (
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          {t('sapCust.lastSync')}: <strong>{status.last_sync.finished_at || status.last_sync.started_at || '—'}</strong>
          {' · '}{t('sapCust.statusLabel')}: <strong>{status.last_sync.status}</strong>
          {status.last_sync.kb_indexed ? ` · KB: ${status.last_sync.kb_indexed} ${t('sapCust.docs')}` : ''}
          {status.fm ? ` · FM: ${status.fm}` : ''}
          {conn ? ` · ${conn.sid}` : ''}
        </div>
      )}

      {/* ── Jadwal otomatis ──
          Ditaruh SETELAH info snapshot dan SEBELUM pemisah tampilan: jadwal
          menyangkut seluruh halaman (metadata + BAdI + transport + source),
          bukan salah satu tab, jadi menempelkannya di dalam tab mana pun akan
          menyesatkan. Komponennya sama dengan yang dipakai Monthly Report —
          `api` di halaman ini bersignature identik dengan useApi() di tmShared,
          jadi bisa diteruskan langsung.

          runConfirm SENGAJA diisi peringatan spesifik, tidak memakai default.
          Job ini melakukan puluhan panggilan SOAP ke SAP PRODUKSI dan syncSource
          pernah mengunci akun SERVICE di D01/130 — Run Now di sini tidak
          setara dengan Run Now yang cuma mengirim email. */}
      {isAdmin && (
      <CronScheduler api={api} jobKey="sap_customizing"
        runConfirm={t('sapCust.cronRunConfirm')} />)}

      {/* ── Pembersihan knowledge base ──
          Sync hanya MENULIS dan MEMPERBARUI; ia tidak pernah menghapus. Ketika
          indexer berhenti menghasilkan sebuah dokumen, dokumen lama tetap hidup
          di KB dan terus dijawab AI tanpa error apa pun. Cron mingguan kini
          menjalankan prune sendiri di langkah terakhir; tombol ini untuk saat
          perlu membersihkan tanpa menunggu jadwal.

          Rencana ditampilkan DULU, dan yang ditampilkan adalah koneksi yang
          DIPERTAHANKAN — bukan yang akan dibuang. Jumlah yang dibuang hanya
          diketahui ai-service saat prune berjalan, jadi menjanjikan angka itu
          di muka akan menjadi janji yang tidak bisa ditepati. */}
      {isAdmin && (
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.85rem 1rem', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>🧹 {t('sapCust.kbPrune.title')}</strong>
          <button onClick={loadPrunePlan} disabled={pruneBusy}
            style={{ padding: '0.4rem 0.8rem', borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: '1px solid #cbd5e1', background: '#fff', color: '#475569',
              cursor: pruneBusy ? 'wait' : 'pointer' }}>
            {t('sapCust.kbPrune.check')}
          </button>
        </div>
        <p style={{ fontSize: 12, color: '#64748b', margin: '0.5rem 0 0' }}>
          {t('sapCust.kbPrune.hint')}
        </p>

        {prunePlan && (
          <div style={{ marginTop: 10, padding: '0.7rem 0.85rem', background: '#f8fafc',
            border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#334155', margin: '0 0 0.4rem' }}>
              {t('sapCust.kbPrune.keepTitle')}
            </p>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#475569' }}>
              {prunePlan.rows.map(r => (
                <li key={r.id}>
                  id={r.id} · {r.sid} client {r.client} — {r.enhancements} enhancement, {r.badi} BAdI
                </li>
              ))}
            </ul>
            {!!(prunePlan.empty || []).length && (
              <p style={{ fontSize: 12, color: '#b45309', margin: '0.5rem 0 0' }}>
                ⚠ {t('sapCust.kbPrune.emptyWarn')}: {prunePlan.empty.join(', ')}
              </p>
            )}
            <button onClick={runPrune} disabled={pruneBusy}
              style={{ marginTop: 10, padding: '0.45rem 0.9rem', borderRadius: 8, fontSize: 13,
                fontWeight: 600, border: '1px solid #b91c1c', background: '#fef2f2', color: '#b91c1c',
                cursor: pruneBusy ? 'wait' : 'pointer' }}>
              {pruneBusy ? t('sapCust.kbPrune.running') : t('sapCust.kbPrune.execute')}
            </button>
          </div>
        )}
      </div>
      )}

      </>)}

      {view === 'config' && (
        <>
          {/* Customizing table VALUES sync (cron sap_cfg_sync): 33 business
              customizing tables from D01/130 + T001B from P01/310 into
              sap_spro_cfg_snap. Separate from the sap_customizing job above,
              which pulls repository metadata (exits/BAdIs/source). Read-only. */}
          {isAdmin && (
            <CronScheduler api={api} jobKey="sap_cfg_sync"
              title="⏰ Customizing Table Sync (D01/130 + T001B P01/310)"
              runConfirm="Read the configured customizing tables (incl. FI validation GB9x and Z/Y tcodes) from D01/130 and T001B from P01/310 via RFC_READ_TABLE (read-only). This takes a few minutes. Continue?" />
          )}
          <SAPConfigLayer api={api} t={t} connId={connId} sproFocus={sproFocus} />
        </>
      )}

      {view === 'badi' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12, marginBottom: 14 }}>
            <StatCard label={t('sapCust.badi.stat.total')} value={badiStats.total} />
            {Object.entries(badiStats.byKind).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
              <StatCard key={k} label={BADI_KIND_LABEL[k] || k} value={n}
                color={BADI_KIND_COLOR[k] || '#64748b'} />
            ))}
            {!!badiStats.classicOff && (
              <StatCard label={t('sapCust.badi.stat.inactive')} value={badiStats.classicOff}
                sub={t('sapCust.badi.stat.inactiveSub')} color="#b45309" />
            )}
            {!!badiStats.localTmp && (
              <StatCard label={t('sapCust.badi.stat.local')} value={badiStats.localTmp}
                sub={t('sapCust.badi.stat.localSub')} color="#dc2626" />
            )}
          </div>

          <Card style={{ marginBottom: 14, background: '#fffbeb', borderColor: '#fde68a', padding: '0.7rem 1rem' }}>
            <div style={{ fontSize: 12.5, color: '#92400e', lineHeight: 1.5 }}>
              <strong>{t('sapCust.limitTitle')}</strong> {t('sapCust.badi.limit')}
            </div>
          </Card>

          <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    {['name', 'kind', 'definition', 'descr', 'status', 'class', 'src', 'package'].map(h => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', fontWeight: 700, color: '#475569',
                        borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                        {t(`sapCust.badi.col.${h}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {badi.map((b, i) => (
                    <tr key={`${b.kind}|${b.name}|${i}`}
                      onClick={() => { if (b.impl_class) setBadiDetail(b); }}
                      style={{ borderBottom: '1px solid #f1f5f9',
                        cursor: b.impl_class ? 'pointer' : 'default' }}>
                      <td style={{ padding: '0.55rem 0.75rem', fontFamily: 'monospace', fontWeight: 600, color: '#0f172a' }}>
                        {b.name}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem' }}>
                        <Badge color={BADI_KIND_COLOR[b.kind] || '#64748b'} bg="#f8fafc">
                          {BADI_KIND_LABEL[b.kind] || b.kind}
                        </Badge>
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', fontFamily: 'monospace', color: '#334155' }}>
                        {b.definition || '—'}
                        {!!b.filter_val && (
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>filter: {b.filter_val}</div>
                        )}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', color: '#475569', maxWidth: 260 }}>
                        {b.descr || '—'}
                      </td>
                      {/* Status HANYA bermakna untuk BAdI klasik. Untuk kategori ENH*
                          sumbernya tidak menyimpan flag aktivasi, jadi ditulis apa
                          adanya sebagai "terpasang" — bukan diklaim aktif. */}
                      <td style={{ padding: '0.55rem 0.75rem' }}>
                        {b.kind === 'badi_classic' ? (
                          b.is_active
                            ? <Badge color="#15803d" bg="#f0fdf4">{t('sapCust.active')}</Badge>
                            : <Badge color="#b45309" bg="#fffbeb">{t('sapCust.inactive')}</Badge>
                        ) : (
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('sapCust.badi.installed')}</span>
                        )}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', fontFamily: 'monospace', fontSize: 11.5, color: '#475569' }}>
                        {b.impl_class || '—'}
                      </td>
                      {/* Status source dibaca dari kolom src_state, BUKAN dari ada
                          atau tidaknya baris di sap_source_line: 'denied',
                          'missing', dan 'error' sama-sama menghasilkan nol baris,
                          dan menyatukannya jadi "tidak ada kode" adalah kesimpulan
                          yang salah tentang sistem SAP-nya. */}
                      <td style={{ padding: '0.55rem 0.75rem', whiteSpace: 'nowrap' }}>
                        {!b.impl_class ? <span style={{ fontSize: 11, color: '#cbd5e1' }}>—</span>
                          : !b.src_state ? <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('sapCust.badi.srcNever')}</span>
                          : (
                            <Badge color={SRC_STATE_COLOR[b.src_state] || '#64748b'} bg="#f8fafc">
                              {t(`sapCust.badi.src.${b.src_state}`)}
                              {b.src_state === 'code' ? ` · ${b.src_code}` : ''}
                            </Badge>
                          )}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', fontFamily: 'monospace', fontSize: 11.5 }}>
                        {String(b.package || '').toUpperCase() === '$TMP'
                          ? <Badge color="#b91c1c" bg="#fef2f2">$TMP</Badge>
                          : (b.package || '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {view === 'enh' && (
      <>
      {/* ── Filter ── */}
      <Card style={{ marginBottom: 12, padding: '0.75rem 1rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={t('sapCust.searchPh')}
            style={{ flex: '1 1 240px', padding: '0.45rem 0.7rem', borderRadius: 8,
              border: '1px solid #cbd5e1', fontSize: 13 }}
          />
          <select value={fType} onChange={e => setFType(e.target.value)}
            style={{ padding: '0.45rem 0.7rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}>
            <option value="">{t('sapCust.allTypes')}</option>
            {Object.keys(stats.byType).sort().map(ty => (
              <option key={ty} value={ty}>{TYPE_LABEL[ty] || `Tipe ${ty || '?'}`}</option>
            ))}
          </select>
          <select value={fProject} onChange={e => setFProject(e.target.value)}
            style={{ padding: '0.45rem 0.7rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, maxWidth: 200 }}>
            <option value="">{t('sapCust.allProjects')}</option>
            {projects.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {!!modules.length && (
            <select value={fModule} onChange={e => setFModule(e.target.value)}
              style={{ padding: '0.45rem 0.7rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, maxWidth: 220 }}>
              <option value="">{t('sapCust.allModules')}</option>
              {modules.map(m => (
                <option key={m} value={m}>
                  {MODULE_NAME[m] ? `${m} — ${MODULE_NAME[m]}` : m} ({stats.byModule[m]})
                </option>
              ))}
              {!!stats.noModule && (
                <option value="(none)">{t('sapCust.noModule')} ({stats.noModule})</option>
              )}
            </select>
          )}
          {!!Object.keys(stats.byState).length && (
            <select value={fState} onChange={e => setFState(e.target.value)}
              style={{ padding: '0.45rem 0.7rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}>
              <option value="">{t('sapCust.allStates')}</option>
              {['code', 'empty', 'missing', 'denied', 'error'].filter(k => stats.byState[k]).map(k => (
                <option key={k} value={k}>{STATE_LABEL[k]} ({stats.byState[k]})</option>
              ))}
            </select>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />
            {t('sapCust.activeOnly')}
          </label>
          <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
            {filtered.length} / {rows.length}
          </span>
        </div>
      </Card>

      {/* ── Tabel ── */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                {['component', 'type', 'module', 'enhancement', 'project', 'include', 'srcState', 'transport', 'changed']
                  .map(h => (
                    <th key={h}
                      onClick={() => toggleSort(h)}
                      style={{ padding: '0.6rem 0.75rem', fontWeight: 700,
                        color: sortKey === h ? 'rgb(var(--brand-600))' : '#475569',
                        borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
                        cursor: 'pointer', userSelect: 'none' }}>
                      {t(`sapCust.col.${h}`)}
                      <span style={{ marginLeft: 4, fontSize: 10, opacity: sortKey === h ? 1 : 0.25 }}>
                        {sortKey === h ? (sortDir === 'asc' ? '▲' : '▼') : '▼'}
                      </span>
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8' }}>
                  {t('common.loading')}
                </td></tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr><td colSpan={9} style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8' }}>
                  {rows.length === 0 ? t('sapCust.empty') : t('common.noData')}
                </td></tr>
              )}
              {!loading && sorted.map((r, i) => {
                const inc = parseArr(r.includes);
                return (
                  <tr key={`${r.project}|${r.enhancement}|${r.component}|${i}`}
                    onClick={() => setDetail(r)}
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                      background: r.is_active ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '0.55rem 0.75rem', fontFamily: 'monospace', fontWeight: 600, color: '#0f172a' }}>
                      {r.component}
                      {!r.is_active && <span style={{ marginLeft: 6 }}><Badge color="#9ca3af" bg="#f3f4f6">{t('sapCust.inactive')}</Badge></span>}
                    </td>
                    <td style={{ padding: '0.55rem 0.75rem' }}>
                      <Badge color={TYPE_COLOR[r.comp_type] || '#64748b'} bg="#f8fafc">
                        {TYPE_LABEL[r.comp_type] || r.comp_type || '?'}
                      </Badge>
                    </td>
                    {/* Modul: kode saja di kolom (ruang sempit); nama lengkap di tooltip
                        dan di panel detail. Klik menyaring tabel ke modul tersebut —
                        klik itu sengaja TIDAK membuka detail baris (stopPropagation). */}
                    <td style={{ padding: '0.55rem 0.75rem' }}>
                      {moduleOf(r) ? (
                        <span
                          onClick={e => { e.stopPropagation(); pickModule(moduleOf(r)); }}
                          title={[MODULE_NAME[moduleOf(r)], r.app_comp_text, r.app_component]
                            .filter(Boolean).join(' · ')}
                          style={{ cursor: 'pointer' }}>
                          <Badge color="#0e7490" bg="#ecfeff">{moduleOf(r)}</Badge>
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#cbd5e1' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '0.55rem 0.75rem' }}>
                      <div style={{ fontFamily: 'monospace', color: '#334155' }}>{r.enhancement}</div>
                      {r.enh_text && <div style={{ fontSize: 11, color: '#94a3b8', maxWidth: 340 }}>{r.enh_text}</div>}
                    </td>
                    <td style={{ padding: '0.55rem 0.75rem', fontFamily: 'monospace', color: '#475569' }}>{r.project || '—'}</td>
                    <td style={{ padding: '0.55rem 0.75rem', fontFamily: 'monospace', color: '#0d9488' }}>
                      {inc.length ? inc.join(', ') : '—'}
                    </td>
                    <td style={{ padding: '0.55rem 0.75rem', whiteSpace: 'nowrap' }}>
                      {r.src_state ? (
                        <>
                          <Badge color={(STATE_COLOR[r.src_state] || {}).c || '#64748b'}
                                 bg={(STATE_COLOR[r.src_state] || {}).bg || '#f1f5f9'}>
                            {STATE_LABEL[r.src_state] || r.src_state}
                          </Badge>
                          {r.src_state === 'code' && (
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                              {r.src_code} / {r.src_lines} {t('sapCust.linesShort')}
                            </div>
                          )}
                          {r.src_state === 'empty' && r.src_lines > 0 && (
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                              {r.src_lines} {t('sapCust.commentOnly')}
                            </div>
                          )}
                        </>
                      ) : (
                        <span style={{ fontSize: 11, color: '#cbd5e1' }}>{t('sapCust.srcNotRead')}</span>
                      )}
                    </td>
                    <td style={{ padding: '0.55rem 0.75rem', whiteSpace: 'nowrap' }}>
                      {(() => {
                        const tr = trSummary[parseArr(r.includes)[0]];
                        if (!tr) return <span style={{ fontSize: 11, color: '#cbd5e1' }}>{t('sapCust.trNone')}</span>;
                        // Yang paling perlu terlihat sekilas: sistem yang BELUM
                        // menerima perubahan terakhir. Itu yang bisa ditindaklanjuti.
                        return (
                          <>
                            {tr.missing && tr.missing.length ? (
                              <Badge color="#b91c1c" bg="#fef2f2">
                                {t('sapCust.trMissing').replace('{sids}', tr.missing.join(', '))}
                              </Badge>
                            ) : (
                              <Badge color="#15803d" bg="#f0fdf4">{t('sapCust.trAll')}</Badge>
                            )}
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontFamily: 'monospace' }}>
                              {tr.trkorr}
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '0.55rem 0.75rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {r.changed_by || '—'}<br />
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>{sapDate(r.changed_on)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      </>
      )}

      {/* ── Drawer detail ──
          Sengaja DI LUAR blok view: drawer hanya terbuka dari tabel enhancement,
          dan menutupnya bersama pergantian tampilan akan membuat isinya hilang
          mendadak tanpa sebab yang jelas bagi user. */}
      {detail && (
        <div
          onClick={() => setDetail(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 60,
            display: 'flex', justifyContent: 'flex-end' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: 'min(560px, 100%)', height: '100%', background: '#fff',
              padding: '1.25rem', overflowY: 'auto', boxShadow: '-4px 0 20px rgba(0,0,0,0.12)' }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                  {detail.component}
                </div>
                <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 3 }}>{detail.enh_text || '—'}</div>
              </div>
              <button onClick={() => setDetail(null)}
                style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, padding: '0.35rem 0.6rem',
                  cursor: 'pointer', fontSize: 13, color: '#475569' }}>
                {t('common.close')}
              </button>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
              <Badge color={TYPE_COLOR[detail.comp_type] || '#64748b'} bg="#f8fafc">
                {TYPE_LABEL[detail.comp_type] || detail.comp_type || '?'}
              </Badge>
              {detail.is_active
                ? <Badge color="#15803d" bg="#f0fdf4">{t('sapCust.active')}</Badge>
                : <Badge color="#9ca3af" bg="#f3f4f6">{t('sapCust.inactive')}</Badge>}
              {detail.src_state && (
                <Badge color={(STATE_COLOR[detail.src_state] || {}).c || '#64748b'}
                       bg={(STATE_COLOR[detail.src_state] || {}).bg || '#f1f5f9'}>
                  {STATE_LABEL[detail.src_state] || detail.src_state}
                </Badge>
              )}
            </div>

            {/* Peringatan paling penting di halaman ini: project AKTIF tetapi
                include-nya KOSONG. Tanpa ini, tabel di bawah (yang menampilkan
                komentar) mudah disalahartikan sebagai "ada logikanya". */}
            {detail.is_active && detail.src_state === 'empty' && (
              <div style={{ marginBottom: 14, padding: '0.7rem 0.9rem', background: '#fffbeb',
                border: '1px solid #fde68a', borderRadius: 10 }}>
                <div style={{ fontSize: 12.5, color: '#92400e', lineHeight: 1.5 }}>
                  <strong>{t('sapCust.emptyWarnTitle')}</strong> {t('sapCust.emptyWarnBody')}
                </div>
              </div>
            )}

            {[
              ['sapCust.d.module',      moduleOf(detail)
                ? (MODULE_NAME[moduleOf(detail)] ? `${moduleOf(detail)} — ${MODULE_NAME[moduleOf(detail)]}` : moduleOf(detail))
                : ''],
              ['sapCust.d.appComponent', detail.app_component
                ? (detail.app_comp_text ? `${detail.app_component} — ${detail.app_comp_text}` : detail.app_component)
                : ''],
              ['sapCust.d.enhancement', detail.enhancement],
              ['sapCust.d.project',     detail.project],
              ['sapCust.d.funcGroup',   detail.func_group],
              ['sapCust.d.include',     parseArr(detail.includes).join(', ')],
              ['sapCust.d.fgIncludes',  parseArr(detail.fg_includes).join(', ')],
              ['sapCust.d.badi',        detail.badi_imp],
              ['sapCust.d.package',     detail.package],
              ['sapCust.d.projPackage', detail.proj_package],
              ['sapCust.d.createdBy',   detail.created_by ? `${detail.created_by} · ${sapDate(detail.created_on)}` : ''],
              ['sapCust.d.activatedBy', detail.activated_by ? `${detail.activated_by} · ${sapDate(detail.activated_on)}` : ''],
              ['sapCust.d.changedBy',   detail.changed_by ? `${detail.changed_by} · ${sapDate(detail.changed_on)}` : ''],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', gap: 10, padding: '0.4rem 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ width: 170, fontSize: 12, color: '#64748b', fontWeight: 600, flexShrink: 0 }}>{t(k)}</div>
                <div style={{ fontSize: 12.5, fontFamily: 'monospace', color: '#0f172a', wordBreak: 'break-word' }}>{v}</div>
              </div>
            ))}

            {/* ── Riwayat transport ─────────────────────────────────────────
                Menjawab: perubahan ini sudah sampai Q01/P01 belum, dan kapan.
                Dibaca dari TPALOG di TIAP sistem tujuan — VRSD di target kosong
                karena VERS_AT_IMP mati, dan TMSBUFFER tidak punya kolom waktu. */}
            {parseArr(detail.includes)[0] && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                  {t('sapCust.trTitle')}
                </div>

                {trLoading && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>{t('common.loading')}</div>}

                {!trLoading && (!trDetail || !(trDetail.versions || []).length) && (
                  <div style={{ fontSize: 12.5, color: '#64748b' }}>{t('sapCust.trNeedSync')}</div>
                )}

                {!trLoading && trDetail && (trDetail.versions || []).slice(0, 8).map((v, vi) => {
                  const srcSid = String(v.trkorr || '').slice(0, 3).toUpperCase();
                  return (
                    <div key={`${v.trkorr}-${vi}`} style={{ marginBottom: 10, padding: '0.6rem 0.8rem',
                      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <code style={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>{v.trkorr}</code>
                        <span style={{ fontSize: 11.5, color: '#64748b' }}>
                          {t('sapCust.trChanged')} {sapDate(v.changed_on)} · {v.author || '—'}
                        </span>
                      </div>
                      <div style={{ marginTop: 6 }}>
                        {(v.imports || []).map((im, ii) => {
                          // Di sistem ASAL, TPALOG mencatat EKSPOR — bukan impor.
                          // Menyebutnya "impor" akan menyiratkan TR diimpor ke
                          // tempat ia dibuat.
                          const isSrc = String(im.sid || '').toUpperCase() === srcSid;
                          const rc = parseInt(String(im.max_retcode || '0'), 10) || 0;
                          const rcColor = rc >= 8 ? '#b91c1c' : (rc >= 4 ? '#b45309' : '#15803d');
                          return (
                            <div key={ii} style={{ display: 'flex', gap: 8, fontSize: 12,
                              padding: '2px 0', alignItems: 'baseline' }}>
                              <span style={{ width: 42, fontWeight: 700, color: '#334155' }}>{im.sid || '?'}</span>
                              <span style={{ width: 46, fontSize: 11, color: '#94a3b8' }}>{im.environment || ''}</span>
                              <span style={{ width: 44, fontSize: 11, color: '#64748b' }}>
                                {isSrc ? t('sapCust.trRelease') : t('sapCust.trImport')}
                              </span>
                              <span style={{ fontFamily: 'monospace', color: '#0f172a' }}>
                                {im.imported_at || '—'}
                              </span>
                              <span style={{ color: rcColor, fontSize: 11 }}>
                                rc={im.max_retcode || '—'}{rc >= 8 ? ' ⚠' : ''}
                              </span>
                            </div>
                          );
                        })}
                        {!(v.imports || []).length && (
                          <div style={{ fontSize: 12, color: '#b45309' }}>{t('sapCust.trNoImport')}</div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Peringatan paling penting: perubahan terakhir belum sampai ke
                    sistem tertentu. Objek yang HILANG menimbulkan error yang
                    kelihatan; objek yang TERTINGGAL VERSI berjalan diam-diam
                    dengan logika lama, dan itu jauh lebih sulit disadari. */}
                {(() => {
                  const sum = trSummary[parseArr(detail.includes)[0]];
                  if (!sum || !sum.missing || !sum.missing.length) return null;
                  return (
                    <div style={{ marginTop: 6, padding: '0.7rem 0.9rem', background: '#fef2f2',
                      border: '1px solid #fecaca', borderRadius: 10 }}>
                      <div style={{ fontSize: 12.5, color: '#b91c1c', lineHeight: 1.5 }}>
                        <strong>{t('sapCust.trGapTitle')}</strong>{' '}
                        {t('sapCust.trGapBody')
                          .replace('{sids}', sum.missing.join(', '))
                          .replace('{tr}', sum.trkorr)}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── Source ABAP ───────────────────────────────────────────────
                Ditampilkan untuk src_lines > 0 apa pun statusnya, termasuk
                'empty'. Untuk include KOSONG isinya justru sering informatif:
                komentar yang tersisa memperlihatkan logika yang pernah ada dan
                kemudian dimatikan (mis. ZXCOZU01, 60 baris tanpa satu pun kode). */}
            {parseArr(detail.includes)[0] && (
              <div style={{ marginTop: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                    {t('sapCust.srcTitle')}
                  </div>
                  <code style={{ fontSize: 12, color: '#0d9488' }}>{parseArr(detail.includes)[0]}</code>
                  {detail.src_read_at && (
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
                      {t('sapCust.srcReadAt')} {String(detail.src_read_at).slice(0, 10)}
                    </span>
                  )}
                  {/* Copy diletakkan di header yang SUDAH ADA, bukan sebagai bilah
                      baru di dalam blok kode seperti MarkdownRenderer — bilah baru
                      akan menduplikasi nama include dan tanggal baca yang sudah
                      tampil di sini. */}
                  <CopySourceButton lines={srcLines} copied={srcCopied} setCopied={setSrcCopied} t={t}
                    style={{ marginLeft: detail.src_read_at ? 0 : 'auto' }} />
                </div>

                {detail.src_trunc > 0 && (
                  <div style={{ fontSize: 11.5, color: '#b45309', marginBottom: 6 }}>
                    ⚠ {t('sapCust.srcTrunc').replace('{n}', detail.src_trunc)}
                  </div>
                )}

                {srcLoading && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>{t('common.loading')}</div>}
                {srcErr && <div style={{ fontSize: 12.5, color: '#b91c1c' }}>{srcErr}</div>}

                {!srcLoading && !srcErr && !detail.src_state && (
                  <div style={{ fontSize: 12.5, color: '#64748b' }}>
                    {t('sapCust.srcNeedSync')}
                  </div>
                )}

                {!srcLoading && !srcErr && detail.src_state && !srcLines.length && (
                  <div style={{ fontSize: 12.5, color: '#64748b' }}>
                    {detail.src_state === 'missing' ? t('sapCust.srcMissing')
                      : detail.src_state === 'denied' ? t('sapCust.srcDenied')
                      : t('sapCust.srcNone')}
                  </div>
                )}

                <SourceTable lines={srcLines} />
              </div>
            )}

            <div style={{ marginTop: 16, padding: '0.8rem 1rem', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: 10 }}>
              <div style={{ fontSize: 12.5, color: '#92400e', lineHeight: 1.55 }}>
                <strong>{t('sapCust.limitTitle')}</strong>{' '}
                {detail.src_state === 'code' ? t('sapCust.limitSrcLive') : t('sapCust.limitDetail')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Drawer source BAdI ────────────────────────────────────────────────
          Terpisah dari drawer enhancement, bukan satu drawer dengan percabangan:
          bentuk datanya berbeda (satu include tetap vs banyak program di bawah
          satu kelas), dan menyatukannya membuat separuh medannya selalu kosong. */}
      {badiDetail && (
        <div
          onClick={() => setBadiDetail(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 60,
            display: 'flex', justifyContent: 'flex-end' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: 'min(680px, 100%)', height: '100%', background: '#fff',
              padding: '1.25rem', overflowY: 'auto', boxShadow: '-4px 0 20px rgba(0,0,0,0.12)' }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                  {badiDetail.name}
                </div>
                <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 3 }}>
                  {badiDetail.descr || '—'}
                </div>
              </div>
              <button onClick={() => setBadiDetail(null)}
                style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, padding: '0.35rem 0.6rem',
                  cursor: 'pointer', fontSize: 13, color: '#475569' }}>
                {t('common.close')}
              </button>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
              <Badge color={BADI_KIND_COLOR[badiDetail.kind] || '#64748b'} bg="#f8fafc">
                {BADI_KIND_LABEL[badiDetail.kind] || badiDetail.kind}
              </Badge>
              {/* Status aktivasi HANYA bermakna untuk BAdI klasik — sama seperti
                  di tabel. Untuk kategori ENH* sumbernya tidak menyimpan flag. */}
              {badiDetail.kind === 'badi_classic' && (
                badiDetail.is_active
                  ? <Badge color="#15803d" bg="#f0fdf4">{t('sapCust.active')}</Badge>
                  : <Badge color="#b45309" bg="#fffbeb">{t('sapCust.inactive')}</Badge>
              )}
              {!!badiDetail.src_state && (
                <Badge color={SRC_STATE_COLOR[badiDetail.src_state] || '#64748b'} bg="#f8fafc">
                  {t(`sapCust.badi.src.${badiDetail.src_state}`)}
                </Badge>
              )}
            </div>

            <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.7, marginBottom: 14 }}>
              <div>
                <span style={{ color: '#94a3b8' }}>{t('sapCust.badi.col.definition')}: </span>
                <code>{badiDetail.definition || '—'}</code>
              </div>
              <div>
                <span style={{ color: '#94a3b8' }}>{t('sapCust.badi.col.class')}: </span>
                <code>{badiDetail.impl_class || '—'}</code>
              </div>
              {!!badiDetail.src_pool && (
                <div>
                  <span style={{ color: '#94a3b8' }}>{t('sapCust.badiSrc.pool')}: </span>
                  <code style={{ color: '#0d9488' }}>{badiDetail.src_pool}</code>
                </div>
              )}
              {!!badiDetail.src_note && (
                <div style={{ color: '#b45309' }}>{badiDetail.src_note}</div>
              )}
            </div>

            {!badiDetail.src_state && (
              <div style={{ fontSize: 12.5, color: '#64748b' }}>{t('sapCust.badiSrc.needSync')}</div>
            )}

            {!!badiDetail.src_state && badiDetail.src_state !== 'code' && badiDetail.src_state !== 'empty' && (
              <div style={{ fontSize: 12.5, color: '#64748b' }}>
                {badiDetail.src_state === 'missing' ? t('sapCust.srcMissing')
                  : badiDetail.src_state === 'denied' ? t('sapCust.srcDenied')
                  : t('sapCust.srcNone')}
              </div>
            )}

            {!!parseArr(badiDetail.src_progs).length && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                    {t('sapCust.srcTitle')}
                  </div>
                  <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
                    {t('sapCust.badiSrc.progs')
                      .replace('{n}', parseArr(badiDetail.src_progs).length)
                      .replace('{lines}', badiDetail.src_lines || 0)
                      .replace('{code}', badiDetail.src_code || 0)}
                  </span>
                  {!!badiDetail.src_read_at && (
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
                      {t('sapCust.srcReadAt')} {String(badiDetail.src_read_at).slice(0, 10)}
                    </span>
                  )}
                  <CopySourceButton lines={badiSrcLines} copied={badiSrcCopied}
                    setCopied={setBadiSrcCopied} t={t}
                    style={{ marginLeft: badiDetail.src_read_at ? 0 : 'auto' }} />
                </div>

                {/* Pemilih program. Class pool ditandai supaya jelas bahwa isinya
                    memang hanya kerangka — tanpa penanda, orang membacanya sebagai
                    "BAdI ini nyaris tidak berisi apa-apa". */}
                <select
                  value={badiProg}
                  onChange={e => setBadiProg(e.target.value)}
                  style={{ width: '100%', padding: '0.45rem 0.7rem', borderRadius: 8,
                    border: '1px solid #cbd5e1', fontSize: 12.5, marginBottom: 8,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}
                >
                  {parseArr(badiDetail.src_progs).map(p => (
                    <option key={p} value={p}>
                      {p}{String(p).toUpperCase() === String(badiDetail.src_pool || '').toUpperCase()
                        ? `  (${t('sapCust.badiSrc.poolTag')})` : ''}
                    </option>
                  ))}
                </select>

                {badiDetail.src_trunc > 0 && (
                  <div style={{ fontSize: 11.5, color: '#b45309', marginBottom: 6 }}>
                    ⚠ {t('sapCust.srcTrunc').replace('{n}', badiDetail.src_trunc)}
                  </div>
                )}

                {badiSrcLoading && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>{t('common.loading')}</div>}
                {badiSrcErr && <div style={{ fontSize: 12.5, color: '#b91c1c' }}>{badiSrcErr}</div>}
                {!badiSrcLoading && !badiSrcErr && !badiSrcLines.length && (
                  <div style={{ fontSize: 12.5, color: '#64748b' }}>{t('sapCust.srcNone')}</div>
                )}

                <SourceTable lines={badiSrcLines} />
              </div>
            )}

            <div style={{ marginTop: 16, padding: '0.8rem 1rem', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: 10 }}>
              <div style={{ fontSize: 12.5, color: '#92400e', lineHeight: 1.55 }}>
                <strong>{t('sapCust.limitTitle')}</strong>{' '}
                {badiDetail.src_state === 'code' ? t('sapCust.limitSrcLive') : t('sapCust.badi.limit')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
